import { useEffect, useMemo, useState } from "react";
import { X, Check, PackageCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import VendorFormDialog from "./VendorFormDialog";

/**
 * Direct Stock Inward — receive parts into stock WITHOUT a purchase order.
 * Creates a GRN, increments stock, writes `direct_inward` movements.
 */
export default function DirectInwardDialog({ open, onClose, onSaved }) {
  const [vendors, setVendors] = useState([]);
  const [parts, setParts] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [invoice, setInvoice] = useState("");
  const [invDate, setInvDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([freshLine()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newVendorOpen, setNewVendorOpen] = useState(false);

  const loadVendors = () =>
    api.get("/spares/vendors", { params: { active_only: true } })
       .then((r) => setVendors(r.data || []));

  useEffect(() => {
    if (!open) return;
    setVendorId(""); setInvoice(""); setInvDate(""); setNotes("");
    setLines([freshLine()]); setError(null);
    loadVendors();
    api.get("/spares/parts", { params: { limit: 1000 } }).then((r) => setParts(r.data || []));
  }, [open]);

  if (!open) return null;

  const setLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, freshLine()]);
  const removeLine = (idx) => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls));

  const canSubmit = lines.some((l) => l.part_id && Number(l.qty) > 0);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const payload = {
        vendor_id: vendorId || null,
        invoice_number: invoice || "", invoice_date: invDate || null, notes,
        lines: lines
          .filter((l) => l.part_id && Number(l.qty) > 0)
          .map((l) => ({ part_id: l.part_id, qty: Number(l.qty),
                         rate: l.rate === "" || l.rate == null ? null : Number(l.rate),
                         notes: l.notes || "" })),
      };
      const r = await api.post("/spares/stock-inwards", payload);
      toast.success(`Received · ${r.data.number}`);
      onSaved?.(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not receive stock");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="inward-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
           data-testid="inward-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#F7F9FC]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#12B76A]/15 text-[#0F9256] grid place-items-center">
              <PackageCheck size={14} />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-[#1d3557]">Direct Stock Inward</h2>
              <p className="text-[11px] text-[#667085]">Quick receive without a purchase order (e.g. local cash purchase).</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-[#E4E7EC] grid place-items-center text-[#667085]"
                  data-testid="inward-close">
            <X size={17} />
          </button>
        </header>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Vendor</span>
              <div className="mt-1 flex gap-2 items-center">
                <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                        data-testid="inward-vendor"
                        className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] flex-1 bg-white">
                  <option value="">— Optional —</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <button type="button" onClick={() => setNewVendorOpen(true)}
                        data-testid="inward-add-vendor"
                        className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#e63946] hover:bg-[#F1FAEE] inline-flex items-center gap-1 whitespace-nowrap">
                  <Plus size={12} /> New
                </button>
              </div>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Invoice #</span>
              <input value={invoice} onChange={(e) => setInvoice(e.target.value)}
                     data-testid="inward-invoice-number"
                     className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Invoice date</span>
              <input type="date" value={invDate} onChange={(e) => setInvDate(e.target.value)}
                     className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </label>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Parts</div>
              <button type="button" onClick={addLine} data-testid="inward-add-line"
                      className="h-7 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] inline-flex items-center gap-1">
                <Plus size={11} /> Add
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((ln, idx) => (
                <InwardLineRow key={idx} idx={idx} ln={ln} parts={parts}
                               onChange={(patch) => setLine(idx, patch)}
                               onRemove={() => removeLine(idx)}
                               removable={lines.length > 1} />
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Notes</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>
          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="inward-error">
              {error}
            </div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={submit} disabled={!canSubmit || saving}
                  data-testid="inward-submit"
                  className="h-10 px-5 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Receiving…" : "Receive"}
          </button>
        </footer>
      </div>
      <VendorFormDialog
        open={newVendorOpen}
        onClose={() => setNewVendorOpen(false)}
        onSaved={async () => {
          setNewVendorOpen(false);
          const r = await api.get("/spares/vendors", { params: { active_only: true } });
          const list = r.data || [];
          setVendors(list);
          // Auto-select the newest by created_at, if the field exists
          const newest = [...list].sort(
            (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))
          )[0];
          if (newest) setVendorId(newest.id);
          toast.success(`Vendor added${newest ? ` — ${newest.name}` : ""}`);
        }}
      />
    </div>
  );
}

function InwardLineRow({ idx, ln, parts, onChange, onRemove, removable }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const results = q
    ? parts.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase()) ||
                          (p.part_number || "").toLowerCase().includes(q.toLowerCase())).slice(0, 8)
    : parts.slice(0, 8);
  return (
    <div className="rounded-lg border border-[#E4E7EC] p-3" data-testid={`inward-line-${idx}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-6 relative">
          <input value={ln.part_id ? ln.name : q}
                 onFocus={() => setOpen(true)}
                 onBlur={() => setTimeout(() => setOpen(false), 200)}
                 onChange={(e) => { setQ(e.target.value); onChange({ part_id: null, name: e.target.value }); }}
                 placeholder="Search part…"
                 data-testid={`inward-line-${idx}-part`}
                 className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
          {open && results.length > 0 && (
            <ul className="absolute z-10 mt-1 bg-white border border-[#E4E7EC] rounded-md shadow-lg w-full max-h-52 overflow-y-auto text-[12.5px]">
              {results.map((p) => (
                <li key={p.id}>
                  <button type="button"
                          onMouseDown={(e) => { e.preventDefault();
                            onChange({ part_id: p.id, name: p.name, part_number: p.part_number || "", rate: p.purchase_price });
                            setOpen(false); setQ(p.name); }}
                          className="w-full text-left px-3 py-1.5 hover:bg-[#F1FAEE]">
                    <div className="font-semibold text-[#1d3557]">{p.name}</div>
                    <div className="text-[10.5px] text-[#667085]">
                      {p.part_number || ""} · in stock {p.stock_qty} · ₹{p.purchase_price || 0}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <input type="number" min={0} value={ln.qty}
               onChange={(e) => onChange({ qty: Number(e.target.value) })}
               placeholder="Qty"
               data-testid={`inward-line-${idx}-qty`}
               className="md:col-span-2 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input type="number" min={0} value={ln.rate ?? ""}
               onChange={(e) => onChange({ rate: e.target.value })}
               placeholder="Rate (opt)"
               className="md:col-span-2 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input value={ln.notes} onChange={(e) => onChange({ notes: e.target.value })}
               placeholder="Notes"
               className="md:col-span-1 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
        <button type="button" onClick={onRemove} disabled={!removable}
                data-testid={`inward-line-${idx}-remove`}
                className="md:col-span-1 w-full h-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#a8dadc] hover:text-[#e63946] hover:border-[#e63946]/40 disabled:opacity-30">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function freshLine() {
  return { part_id: null, name: "", part_number: "", qty: 1, rate: "", notes: "" };
}
